import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { LinkAsPolymorphicLink } from "@faire/slate/private/link/LinkAsPolymorphicLink";
import {
  ILinkAsButtonProps,
  ILinkAsLinkProps,
} from "@faire/slate/private/link/types";
import { isLinkAsLinkProps } from "@faire/slate/private/link/utils";
import React from "react";

// EXCEPTION — do not copy: Link is a bare styling primitive that consumers compose into
// their own text layouts, so className passthrough is intentional. For every other component,
// strip className, style, and dangerouslySetInnerHTML via OmitWebProps<T> instead.
type IProps = (ILinkAsLinkProps | ILinkAsButtonProps) & {
  className?: string;
};

/**
 * Component with basic link styling.
 * This component is intended to be used in context of other components that have text styles.
 *
 * {@link https://www.notion.so/faire/Link-2722efb5c25a8081ab51f1547b31f2d4 | Slate/Link}
 */
export const Link: React.FC<IProps> = (props) => {
  const { className } = props;
  const linkCls = linkStyle.cls({ className: componentCls(className) });
  if (isLinkAsLinkProps(props)) {
    return (
      <LinkAsPolymorphicLink
        data-component-name="Slate/Link"
        {...props}
        className={linkCls}
      />
    );
  }
  const { as: Component } = props;
  if (!Component) {
    return null;
  }
  return (
    <Component
      data-component-name="Slate/Link"
      {...props}
      className={linkCls}
    />
  );
};

const linkStyle = tsv({
  base: [
    "focusable interactive",
    "inline underline underline-offset-[0.25em]",
    "text-inherit",
  ],
});
